import PublicPageLayout from '@/components/feature/PublicPageLayout';

const values = [
  {
    icon: 'ri-heart-pulse-line',
    title: 'Resultado do Cliente',
    description: 'Toda decisão que tomamos é orientada pelo impacto positivo na operação e nos resultados dos nossos clientes.',
  },
  {
    icon: 'ri-lightbulb-flash-line',
    title: 'Inovação Constante',
    description: 'Investimos continuamente em novas tecnologias e integrações para manter nossos usuários à frente da concorrência.',
  },
  {
    icon: 'ri-shield-check-line',
    title: 'Segurança em Primeiro Lugar',
    description: 'Proteção de dados, conformidade com a LGPD e criptografia de ponta a ponta são prioridades absolutas.',
  },
  {
    icon: 'ri-hand-heart-line',
    title: 'Transparência Total',
    description: 'Preços claros, contratos sem letras miúdas e comunicação honesta em todos os canais de atendimento.',
  },
];

export default function AboutPage() {
  const schemaJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'WZap PRO',
    url: 'https://wzappro.com',
    logo: 'https://wzappro.com/favicon.svg',
    description:
      'WZap PRO é a plataforma líder de marketing e automação via WhatsApp Business no Brasil. Conecte sua própria API Meta ou use nossa infraestrutura gerenciada para escalar suas campanhas.',
    foundingDate: '2024',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Tapejara',
      addressRegion: 'RS',
      addressCountry: 'BR',
    },
    email: 'nx3designer@gmail.com',
    telephone: '+5554996542326',
    sameAs: ['https://wa.me/5554996542326'],
  };

  return (
    <PublicPageLayout
      title="Sobre Nós | WZap PRO"
      description="Conheça a história, missão e valores da WZap PRO. Uma plataforma brasileira de marketing via WhatsApp Business, criada para escalar o crescimento de empresas de todos os tamanhos."
      schemaJsonLd={schemaJsonLd}
    >
      <article className="space-y-12">
        {/* Story */}
        <section className="bg-white dark:bg-background-100 rounded-2xl border border-background-200 dark:border-background-300 p-6 md:p-8">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary-50 dark:bg-primary-500/10 border border-primary-200 dark:border-primary-500/20 mb-5">
            <i className="ri-book-open-line text-primary-600 text-sm" />
            <span className="text-xs font-semibold text-primary-700 dark:text-primary-400">Nossa História</span>
          </div>
          <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 dark:text-foreground-100 mb-4">
            Nascemos para democratizar o marketing via WhatsApp
          </h2>
          <p className="text-foreground-600 dark:text-foreground-400 leading-relaxed mb-4">
            A <strong className="text-foreground-800 dark:text-foreground-200">WZap PRO</strong> nasceu em 2024, em Tapejara, no interior do Rio Grande do Sul, com uma missão clara: oferecer às pequenas e médias empresas brasileiras o mesmo poder de comunicação que antes era acessível apenas a grandes corporações.
          </p>
          <p className="text-foreground-600 dark:text-foreground-400 leading-relaxed mb-4">
            Identificamos que milhares de empresas no Brasil utilizavam o WhatsApp de forma artesanal — enviando mensagens uma a uma, sem controle, sem relatórios e sem automação. Decidimos então criar uma plataforma profissional que unisse a simplicidade do WhatsApp com a robustez de um CRM e a eficiência de um sistema de disparo em massa.
          </p>
          <p className="text-foreground-600 dark:text-foreground-400 leading-relaxed">
            Hoje, a WZap PRO atende empresas de diversos segmentos — desde e-commerces e clínicas de saúde até imobiliárias, escolas e agências de marketing — ajudando cada uma delas a escalar sua comunicação, fidelizar clientes e aumentar suas vendas através do canal mais pessoal do mundo: o WhatsApp.
          </p>
        </section>

        {/* Mission, Vision, Values */}
        <section className="bg-white dark:bg-background-100 rounded-2xl border border-background-200 dark:border-background-300 p-6 md:p-8">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent-50 dark:bg-accent-500/10 border border-accent-200 dark:border-accent-500/20 mb-5">
            <i className="ri-compass-3-line text-accent-600 text-sm" />
            <span className="text-xs font-semibold text-accent-700 dark:text-accent-400">Missão, Visão e Valores</span>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="p-5 bg-background-50 dark:bg-background-200/40 rounded-xl border border-background-200 dark:border-background-300">
              <div className="w-10 h-10 rounded-xl bg-primary-50 dark:bg-primary-500/10 flex items-center justify-center mb-3">
                <i className="ri-flag-line text-xl text-primary-600 dark:text-primary-400" />
              </div>
              <h3 className="text-base font-heading font-bold text-foreground-950 dark:text-foreground-100 mb-2">Missão</h3>
              <p className="text-sm text-foreground-600 dark:text-foreground-400 leading-relaxed">
                Democratizar o acesso a ferramentas profissionais de marketing via WhatsApp, permitindo que empresas de todos os portes comuniquem-se em massa de forma personalizada, segura e eficiente.
              </p>
            </div>
            <div className="p-5 bg-background-50 dark:bg-background-200/40 rounded-xl border border-background-200 dark:border-background-300">
              <div className="w-10 h-10 rounded-xl bg-accent-50 dark:bg-accent-500/10 flex items-center justify-center mb-3">
                <i className="ri-eye-line text-xl text-accent-600 dark:text-accent-400" />
              </div>
              <h3 className="text-base font-heading font-bold text-foreground-950 dark:text-foreground-100 mb-2">Visão</h3>
              <p className="text-sm text-foreground-600 dark:text-foreground-400 leading-relaxed">
                Ser a plataforma líder de automação e marketing via WhatsApp Business no Brasil, reconhecida pela inovação, segurança e pelo resultado que entrega aos seus clientes.
              </p>
            </div>
          </div>

          <h3 className="text-base font-heading font-bold text-foreground-950 dark:text-foreground-100 mb-4">Nossos Valores</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            {values.map((v, i) => (
              <div key={i} className="flex items-start gap-3 p-4 bg-background-50 dark:bg-background-200/40 rounded-xl border border-background-200 dark:border-background-300">
                <div className="w-9 h-9 rounded-lg bg-primary-50 dark:bg-primary-500/10 flex items-center justify-center flex-shrink-0">
                  <i className={`${v.icon} text-lg text-primary-600 dark:text-primary-400`} />
                </div>
                <div>
                  <h4 className="text-sm font-heading font-semibold text-foreground-950 dark:text-foreground-100 mb-1">{v.title}</h4>
                  <p className="text-xs text-foreground-500 dark:text-foreground-400 leading-relaxed">{v.description}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Company Data */}
        <section className="bg-white dark:bg-background-100 rounded-2xl border border-background-200 dark:border-background-300 p-6 md:p-8">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-secondary-50 dark:bg-secondary-500/10 border border-secondary-200 dark:border-secondary-500/20 mb-5">
            <i className="ri-building-line text-secondary-600 text-sm" />
            <span className="text-xs font-semibold text-secondary-700 dark:text-secondary-400">Dados da Empresa</span>
          </div>
          <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 dark:text-foreground-100 mb-5">
            Informações corporativas
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="p-4 bg-background-50 dark:bg-background-200/40 rounded-xl border border-background-200 dark:border-background-300">
              <p className="text-xs font-semibold text-foreground-500 dark:text-foreground-400 uppercase tracking-wider mb-1">Razão Social</p>
              <p className="text-sm font-medium text-foreground-800 dark:text-foreground-200">WZap PRO Tecnologia Ltda.</p>
            </div>
            <div className="p-4 bg-background-50 dark:bg-background-200/40 rounded-xl border border-background-200 dark:border-background-300">
              <p className="text-xs font-semibold text-foreground-500 dark:text-foreground-400 uppercase tracking-wider mb-1">CNPJ</p>
              <p className="text-sm font-medium text-foreground-800 dark:text-foreground-200">07.273.736/0001-82</p>
            </div>
            <div className="p-4 bg-background-50 dark:bg-background-200/40 rounded-xl border border-background-200 dark:border-background-300">
              <p className="text-xs font-semibold text-foreground-500 dark:text-foreground-400 uppercase tracking-wider mb-1">Endereço</p>
              <p className="text-sm font-medium text-foreground-800 dark:text-foreground-200">Tapejara, RS — Brasil</p>
            </div>
            <div className="p-4 bg-background-50 dark:bg-background-200/40 rounded-xl border border-background-200 dark:border-background-300">
              <p className="text-xs font-semibold text-foreground-500 dark:text-foreground-400 uppercase tracking-wider mb-1">Email</p>
              <p className="text-sm font-medium text-foreground-800 dark:text-foreground-200">
                <a href="mailto:nx3designer@gmail.com" className="text-primary-600 hover:text-primary-700 underline">nx3designer@gmail.com</a>
              </p>
            </div>
            <div className="p-4 bg-background-50 dark:bg-background-200/40 rounded-xl border border-background-200 dark:border-background-300">
              <p className="text-xs font-semibold text-foreground-500 dark:text-foreground-400 uppercase tracking-wider mb-1">WhatsApp</p>
              <p className="text-sm font-medium text-foreground-800 dark:text-foreground-200">
                <a href="https://wa.me/5554996542326" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:text-primary-700 underline">+55 54 99654-2326</a>
              </p>
            </div>
            <div className="p-4 bg-background-50 dark:bg-background-200/40 rounded-xl border border-background-200 dark:border-background-300">
              <p className="text-xs font-semibold text-foreground-500 dark:text-foreground-400 uppercase tracking-wider mb-1">Website</p>
              <p className="text-sm font-medium text-foreground-800 dark:text-foreground-200">
                <a href="https://wzappro.com" className="text-primary-600 hover:text-primary-700 underline">wzappro.com</a>
              </p>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-primary-500 dark:bg-primary-600 rounded-2xl p-8 md:p-12 text-center">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-white mb-3">
            Pronto para escalar seu marketing via WhatsApp?
          </h2>
          <p className="text-white/80 text-base max-w-lg mx-auto mb-6">
            Junte-se a centenas de empresas brasileiras que já utilizam a WZap PRO para automatizar campanhas e aumentar vendas.
          </p>
          <a
            href="https://wzappro.com/login"
            className="inline-flex items-center justify-center bg-white hover:bg-white/90 text-foreground-950 text-sm font-bold px-8 py-3.5 rounded-xl transition-all duration-200 whitespace-nowrap"
          >
            Começar Agora
            <i className="ri-arrow-right-line ml-2" />
          </a>
        </section>
      </article>
    </PublicPageLayout>
  );
}